/**
 * The screens that have no data source yet.
 *
 * Each is built to the design handoff and filled from `sample.ts`, under a
 * visible "Example content" banner. The point is a navigable skeleton: the
 * shape, spacing and copy are reviewable now, and each screen can be wired to
 * real data one at a time without redrawing it.
 *
 * When a screen becomes real, the pattern is the same every time: delete its
 * slice of `sample.ts`, delete the `previewBanner()` call, and take its data
 * from a prop. Nothing else about the file should need to change.
 *
 * Two departures from the handoff's copy, both deliberate and both recorded in
 * ADR-0038 rather than made quietly:
 *
 *  · The risk bands. The design's are "Likely to be questioned" and a ranking
 *    "by how often each causes a refusal". That is a prediction about what a
 *    caseworker will do, which §3.1.3 prohibits permanently and says survives
 *    authorisation. The screen is built and keeps its whole structure; the
 *    bands describe the EVIDENCE ("Thin at the moment", "Unexplained", "A date
 *    to watch") rather than forecasting an outcome, and each card still ends in
 *    "What would close it".
 *  · "The route Verity recommends" is not rendered. ADR-0022 point 1 prohibits
 *    route recommendation outright.
 */
import { ARROW, el, formatDate, previewBanner, sectionHead } from "./dom.js";
import { SAMPLE } from "./sample.js";

function card(className = ""): HTMLElement {
  return el("section", `card ${className}`.trim());
}

// ------------------------------------------------------------------ documents

export function renderDocuments(): HTMLElement {
  const frag = el("div");
  frag.append(previewBanner("Uploading and reading documents is not built yet."));

  const drop = el("div", "dropzone");
  drop.append(el("p", "dz-title", "Drop files here, or browse"));
  drop.append(
    el(
      "p",
      "dz-note",
      "Verity reads the dates and names on a document and tells you which requirement it covers, so you can see what is still missing rather than guessing.",
    ),
  );
  const browse = el("button", "btn", "Browse files");
  browse.type = "button";
  browse.disabled = true;
  drop.append(browse);
  frag.append(drop);

  const pair = el("div", "pair");

  const translations = card();
  translations.append(el("h3", "card-title", "Needs a certified translation"));
  const tlist = el("ul", "tick-list");
  for (const row of SAMPLE.translations) {
    const li = el("li", row.met ? "met" : "unmet");
    li.append(el("span", "mark", row.met ? "✓" : "○"));
    const body = el("div");
    body.append(el("div", "label", row.label));
    body.append(el("div", "note", row.state));
    li.append(body);
    tlist.append(li);
  }
  translations.append(tlist);
  translations.append(
    el(
      "p",
      "card-foot",
      "A translation must carry the translator's name, the date, and a statement that it is accurate. Verity checks for all three.",
    ),
  );
  pair.append(translations);

  const scans = card();
  scans.append(el("h3", "card-title", "Scan quality"));
  const slist = el("ul", "tick-list");
  for (const row of SAMPLE.scans) {
    const li = el("li", row.ok ? "met" : "unmet");
    li.append(el("span", "mark", row.ok ? "✓" : "!"));
    const body = el("div");
    body.append(el("div", "label", row.label));
    body.append(el("div", "note", row.note));
    li.append(body);
    slist.append(li);
  }
  scans.append(slist);
  scans.append(
    el(
      "p",
      "card-foot",
      "A scan nobody can read is one of the commonest reasons an application comes back asking for more information.",
    ),
  );
  pair.append(scans);
  frag.append(pair);

  frag.append(sectionHead("Everything uploaded"));

  const table = el("div", "doc-table");
  const header = el("div", "doc-row doc-head");
  for (const heading of ["Document", "Covers", "Added", "Verity's check"]) {
    header.append(el("span", undefined, heading));
  }
  table.append(header);

  for (const doc of SAMPLE.documents) {
    const row = el("div", "doc-row");
    const first = el("span");
    first.append(el("span", "doc-name", doc.name));
    first.append(el("span", "doc-meta", `${doc.by} · ${doc.size}`));
    row.append(first);
    row.append(el("span", "doc-covers", doc.covers));
    row.append(el("span", "doc-added", doc.added));
    row.append(el("span", `doc-check ${doc.checkState}`, doc.check));
    table.append(row);
  }
  frag.append(table);
  return frag;
}

// ---------------------------------------------------------------- risk review

export function renderRisk(): HTMLElement {
  const frag = el("div");
  frag.append(
    previewBanner("How weaknesses are found is not built yet, and is an open product question."),
  );

  const head = card("risk-head");
  const left = el("div");
  left.append(el("h2", "risk-title", "Three things worth strengthening before you submit"));
  left.append(
    el(
      "p",
      "risk-lede",
      "None of these means anything is wrong. They are the places where the evidence is thinner than the rest, or where a figure has nothing alongside it to explain it. Each one names what would close it.",
    ),
  );
  head.append(left);

  const legend = el("div", "legend");
  for (const [n, label, cls] of [
    ["1", "Thin at the moment", "thin"],
    ["1", "Unexplained", "unexplained"],
    ["1", "A date to watch", "timing"],
    ["9", "Settled and solid", "solid"],
  ] as [string, string, string][]) {
    const row = el("div", "legend-row");
    row.append(el("span", `dot risk-dot--${cls}`));
    row.append(el("span", "legend-label", label));
    row.append(el("span", "legend-n num", n));
    legend.append(row);
  }
  head.append(legend);
  frag.append(head);

  for (const risk of SAMPLE.risks) {
    const wrapper = el("article", "risk");
    const upper = el("div", "risk-upper");

    const top = el("div", "risk-top");
    top.append(el("span", `dot risk-dot--${risk.band}`));
    top.append(el("span", "risk-band", risk.bandLabel));
    upper.append(top);

    upper.append(el("h3", "risk-h", risk.title));
    upper.append(el("p", "risk-why", risk.reasoning));
    upper.append(el("p", "risk-owner", `For: ${risk.owner}`));
    wrapper.append(upper);

    const lower = el("div", "risk-lower");
    lower.append(el("h4", "risk-fix-h", "What would close it"));
    const list = el("ul", "risk-fixes");
    for (const fix of risk.fixes) {
      const li = el("li");
      li.append(el("span", "arrow", ARROW));
      li.append(el("span", undefined, fix));
      list.append(li);
    }
    lower.append(list);
    wrapper.append(lower);
    frag.append(wrapper);
  }
  return frag;
}

// ------------------------------------------------------------------ ask verity

export function renderAsk(): HTMLElement {
  const layout = el("div", "ask-layout");
  const main = el("div");

  const intro = card();
  intro.append(el("h2", "card-title-lg", "Ask about your application, not the internet"));
  intro.append(
    el(
      "p",
      "card-lede",
      "Answers draw on the answers you have given, the documents you have uploaded, and the rules as they stand today — and each answer says which of those it used.",
    ),
  );

  const inputRow = el("div", "ask-input");
  const input = el("input");
  input.type = "text";
  input.placeholder = "Ask a question about your case";
  input.disabled = true;
  inputRow.append(input);
  const askBtn = el("button", "btn btn-primary", "Ask");
  askBtn.type = "button";
  askBtn.disabled = true;
  inputRow.append(askBtn);
  intro.append(inputRow);
  main.append(intro);

  for (const exchange of SAMPLE.ask.thread) {
    const block = el("div", "exchange");

    const q = el("div", "ask-q");
    q.append(el("span", "avatar avatar--sm", exchange.who.slice(0, 2).toUpperCase()));
    q.append(el("p", undefined, exchange.question));
    block.append(q);

    const a = el("div", "ask-a");
    a.append(el("p", "ask-answer", exchange.answer));
    const drawn = el("div", "drawn");
    drawn.append(el("p", "drawn-label", "Drawn from"));
    const list = el("ul");
    for (const source of exchange.drawnFrom) list.append(el("li", undefined, source));
    drawn.append(list);
    a.append(drawn);
    block.append(a);
    main.append(block);
  }
  layout.append(main);

  const side = el("aside", "ask-side");
  const suggested = card();
  suggested.append(el("h3", "card-title", "People in your position ask"));
  const slist = el("ul", "suggested");
  for (const question of SAMPLE.ask.suggested) {
    const li = el("li");
    const button = el("button", "link-button", question);
    button.type = "button";
    button.disabled = true;
    li.append(button);
    slist.append(li);
  }
  suggested.append(slist);
  side.append(suggested);

  const wont = card("amber-card");
  wont.append(el("h3", "card-title", "When Verity will not answer"));
  wont.append(
    el(
      "p",
      "card-foot",
      "Anything that turns on judgement rather than on the rules is handed to a solicitor or a registered adviser, with a summary of the case attached so you are not starting again.",
    ),
  );
  side.append(wont);
  layout.append(side);

  const frag = el("div");
  frag.append(previewBanner("Asking questions is not built yet."));
  frag.append(layout);
  return frag;
}

// -------------------------------------------------------------------- together

export function renderTogether(): HTMLElement {
  const frag = el("div");
  frag.append(previewBanner("A second person on the case is not built yet."));

  const lanes = el("div", "lanes");
  for (const [person, tasks, colour] of [
    [SAMPLE.people.applicant, SAMPLE.together.applicant, "green"],
    [SAMPLE.people.sponsor, SAMPLE.together.sponsor, "amber"],
  ] as const) {
    const lane = card();
    const head = el("div", "lane-head");
    head.append(el("span", `avatar avatar--${colour}`, person.initials));
    const who = el("div");
    who.append(el("div", "lane-name", person.name));
    who.append(el("div", "lane-place", `${person.place} · ${tasks.filter((t) => !t.done).length} open`));
    head.append(who);
    lane.append(head);

    const list = el("ul", "task-list");
    for (const task of tasks) {
      const li = el("li", task.done ? "done" : "");
      const box = el("span", `checkbox ${task.done ? "checked" : ""}`.trim(), task.done ? "✓" : "");
      li.append(box);
      const body = el("div");
      body.append(el("div", "task-title", task.title));
      body.append(el("div", "task-note", task.note));
      li.append(body);
      list.append(li);
    }
    lane.append(list);
    lanes.append(lane);
  }
  frag.append(lanes);

  frag.append(sectionHead("Waiting on each other"));
  const waiting = el("div", "waiting");
  for (const row of SAMPLE.together.waiting) {
    const item = el("div", "waiting-row");
    const pair = el("div", "waiting-pair");
    pair.append(el("span", "avatar avatar--sm", row.from.slice(0, 2).toUpperCase()));
    pair.append(el("span", "arrow", ARROW));
    pair.append(el("span", "avatar avatar--sm avatar--amber", row.to.slice(0, 2).toUpperCase()));
    item.append(pair);

    const body = el("div", "waiting-body");
    body.append(el("div", "waiting-ask", row.ask));
    body.append(el("div", "waiting-age", `Asked ${row.age} ago`));
    item.append(body);

    const action = el("button", "btn", row.action);
    action.type = "button";
    action.disabled = true;
    item.append(action);
    waiting.append(item);
  }
  frag.append(waiting);
  return frag;
}

// -------------------------------------------------------------- submission day

export function renderSubmission(): HTMLElement {
  const layout = el("div", "submit-layout");
  const main = el("div");

  const head = card("submit-head");
  const left = el("div");
  left.append(el("h2", "card-title-lg", "Submission day, in order"));
  left.append(
    el(
      "p",
      "card-lede",
      "Each step unlocks the one after it, so you cannot pay before the form is right or book biometrics before the slot exists.",
    ),
  );
  head.append(left);
  const counter = el("div", "submit-count");
  counter.append(el("div", "n", "2 of 6"));
  counter.append(el("div", "k", "done"));
  head.append(counter);
  main.append(head);

  SAMPLE.submission.steps.forEach((step, i) => {
    const item = el("article", `step step--${step.state}`);
    const circle = el("span", "step-circle", step.state === "done" ? "✓" : String(i + 1));
    item.append(circle);
    const body = el("div", "step-body");
    body.append(el("div", "step-title", step.title));
    body.append(el("div", "step-note", step.note));
    item.append(body);
    if (step.state === "next") {
      const cta = el("button", "btn btn-primary", "Start this step");
      cta.type = "button";
      cta.disabled = true;
      item.append(cta);
    }
    main.append(item);
  });
  layout.append(main);

  const side = el("aside", "submit-side");
  const fees = card();
  fees.append(el("h3", "card-title", "What it costs"));
  const list = el("ul", "fee-list");
  for (const fee of SAMPLE.submission.fees) {
    const li = el("li");
    li.append(el("span", undefined, fee.label));
    li.append(el("span", "num", fee.amount));
    list.append(li);
  }
  fees.append(list);
  const total = el("div", "fee-total");
  total.append(el("span", undefined, "Total"));
  total.append(el("span", "num", SAMPLE.submission.total));
  fees.append(total);
  fees.append(
    el(
      "p",
      "card-foot",
      "The visa fee and the health surcharge are paid to the Home Office, not to Verity.",
    ),
  );
  side.append(fees);

  const warn = card("amber-card");
  warn.append(el("h3", "card-title", "Do not book biometrics first"));
  warn.append(
    el(
      "p",
      "card-foot",
      "The appointment slot is generated by the form. Booking before the form is finished means booking an appointment the application cannot use.",
    ),
  );
  side.append(warn);
  layout.append(side);

  const frag = el("div");
  frag.append(previewBanner("Submitting an application is not built yet."));
  frag.append(layout);
  return frag;
}

// ------------------------------------------------------------- after you apply

export function renderAfter(): HTMLElement {
  const frag = el("div");
  frag.append(previewBanner("Tracking a submitted application is not built yet."));

  const head = card();
  head.append(el("h2", "card-title-lg", "The waiting is the hardest part, and it is normal"));
  head.append(
    el(
      "p",
      "card-lede",
      "Most of the time between submitting and hearing back is silence. Verity marks each point where something typically happens, so that silence stops reading as bad news.",
    ),
  );
  frag.append(head);

  const timeline = el("div", "timeline");
  for (const row of SAMPLE.after) {
    const item = el("div", `tl-row ${row.quiet ? "tl-quiet" : ""}`.trim());
    item.append(el("div", "tl-when", row.when));
    const rule = el("div", "tl-rule");
    rule.append(el("span", "tl-dot"));
    item.append(rule);
    const body = el("div", "tl-body");
    body.append(el("div", "tl-title", row.title));
    body.append(el("div", "tl-note", row.note));
    item.append(body);
    timeline.append(item);
  }
  frag.append(timeline);

  frag.append(sectionHead("If it does not go smoothly"));
  const pair = el("div", "pair");

  const moreInfo = card();
  moreInfo.append(el("h3", "card-title", "They ask for more information"));
  moreInfo.append(
    el(
      "p",
      "card-foot",
      "This is not a refusal, and it is common. It is usually one document. You normally have ten working days, and the right approach is to answer exactly what was asked and nothing else.",
    ),
  );
  pair.append(moreInfo);

  const refused = card();
  refused.append(el("h3", "card-title", "It is refused"));
  refused.append(
    el(
      "p",
      "card-foot",
      "The refusal letter names the paragraph of the rules it turns on, which tells you what to do next. Administrative review, appeal and applying again are different routes with different deadlines, and which one applies depends on that paragraph.",
    ),
  );
  pair.append(refused);
  frag.append(pair);
  return frag;
}

// ------------------------------------------------------------------- account

export interface AccountPerson {
  id: string;
  kind: "member" | "invitation";
  email: string | null;
  role: string;
  access: string;
  state: "active" | "invited" | "setting_up";
  is_you: boolean;
}

export interface AccountPayment {
  status:
    | "pending"
    | "saved"
    | "action_required"
    | "captured"
    | "expired"
    | "cancelled"
    | "failed";
  display: string;
  tier_name: string;
  card_saved_at: string | null;
  captured_at: string | null;
  /** Where to pick up an unfinished checkout. Null unless there is one. */
  resume_url: string | null;
}

export interface AccountData {
  people: AccountPerson[];
  tier: { name: string; summary: string; display: string } | null;
  payment: AccountPayment | null;
  /** Whether payment is switched on at all. */
  paymentConfigured: boolean;
  onRevoke: (kind: "member" | "invitation", id: string) => Promise<void>;
  onStartPayment: () => Promise<void>;
  /**
   * Erase, leave, or be told the file is retained — the server decides which
   * (F14 D3), so this returns what actually happened rather than a boolean.
   */
  onDelete: () => Promise<"erased" | "left" | "retained" | null>;
}

/**
 * What the payment state means, in the second person.
 *
 * NOTHING IS TAKEN AND NOTHING IS RESERVED until an adviser is assigned
 * (ADR-0050). Every line here has to make that true rather than merely not
 * contradict it: somebody who has just typed their card number wants to know
 * what has happened to their money, and "we have your card" is only reassuring
 * if it also says what we have not done with it.
 *
 * `action_required` is the one that costs an explanation. It means the bank
 * wants the cardholder to confirm a charge nobody was present for — a thing
 * that has gone right up to that point, not a failure.
 */
function paymentLine(payment: AccountPayment | null, configured: boolean): string {
  if (!payment) {
    return configured
      ? "Nothing has been charged. We ask for your card now and keep it on file; the payment is taken only when a registered adviser is assigned to your case."
      : "Nothing has been charged, and there is nothing to pay yet.";
  }
  switch (payment.status) {
    case "pending":
      return "You started this and did not finish. No card has been saved and nothing has been charged.";
    case "saved":
      return "Your card is on file and nothing has been charged. No money is held or reserved — the payment is taken when a registered adviser is assigned to your case.";
    case "action_required":
      return "Your bank wants to check this payment with you before it goes through. Nothing has been taken yet. Your adviser has been assigned and will not be held up by this, but it does need you.";
    case "captured":
      return "Paid.";
    case "expired":
      return "The payment link timed out before it was used. No card was saved and nothing has been charged.";
    case "cancelled":
      return "The card on file was removed. Nothing has been charged.";
    case "failed":
      return "Your card could not be saved. Nothing has been charged, and you can try again with another card.";
  }
}

export interface AccountData {
  people: AccountPerson[];
  tier: { name: string; summary: string; display: string } | null;
  payment: AccountPayment | null;
  /** Whether payment is switched on at all. */
  paymentConfigured: boolean;
  onRevoke: (kind: "member" | "invitation", id: string) => Promise<void>;
  onStartPayment: () => Promise<void>;
  /**
   * Erase, leave, or be told the file is retained — the server decides which
   * (F14 D3), so this returns what actually happened rather than a boolean.
   */
  onDelete: () => Promise<"erased" | "left" | "retained" | null>;
}


/** Initials from an address, since we hold no separate display name yet. */
function initialsOf(email: string | null, role: string): string {
  if (!email) return role === "adviser" ? "A" : "?";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = (parts.length > 1 ? parts[0]![0]! + parts[1]![0]! : local.slice(0, 2)) || "?";
  return letters.toUpperCase();
}

function describeAccess(person: AccountPerson): string {
  const role =
    person.role === "adviser"
      ? "Adviser"
      : person.role === "applicant"
        ? "Applicant"
        : "Sponsor";
  const reach = person.access === "read" ? "can read the case" : "can work on the case";
  const state =
    person.state === "invited"
      ? "invitation sent, not accepted yet"
      : person.state === "setting_up"
        ? "joined, still setting up"
        : "active";
  return `${role} \u00b7 ${reach} \u00b7 ${state}`;
}

export function renderAccount(email: string, initials: string, account: AccountData): HTMLElement {
  const frag = el("div");

  const profile = card("profile");
  profile.append(el("div", "avatar avatar--lg", initials));
  const who = el("div", "profile-body");
  who.append(el("h2", "profile-name", email));
  who.append(el("p", "profile-role", "Signed in with Verity"));
  const grid = el("div", "profile-grid");
  for (const [k, v] of [
    ["Email", email],
    ["Phone", "Not set"],
    ["Time zone", Intl.DateTimeFormat().resolvedOptions().timeZone],
  ] as [string, string][]) {
    const cell = el("div");
    cell.append(el("div", "k", k));
    cell.append(el("div", "v", v));
    grid.append(cell);
  }
  who.append(grid);
  profile.append(who);
  frag.append(profile);

  frag.append(
    sectionHead("People and access", "Anyone listed here can see every document on the case."),
  );

  const people = el("div", "people");
  if (account.people.length === 0) {
    people.append(el("p", "note", "Loading\u2026"));
  }
  for (const person of account.people) {
    const row = el("div", "person-row");
    row.append(el("span", "avatar", initialsOf(person.email, person.role)));

    const body = el("div");
    const name = el("div", "person-name");
    name.append(document.createTextNode(person.email ?? "Invitation sent"));
    if (person.is_you) name.append(el("span", "person-you", "you"));
    body.append(name);
    body.append(el("div", "person-access", describeAccess(person)));
    row.append(body);

    // No control where the answer would always be no. The server refuses to
    // remove you or the other partner, and offering a button that 404s is a
    // worse explanation than not offering one.
    const removable =
      !person.is_you && (person.kind === "invitation" || person.role === "adviser");
    if (removable) {
      const remove = el("button", "btn btn-danger", person.kind === "invitation" ? "Withdraw" : "Remove");
      remove.type = "button";
      remove.addEventListener("click", () => {
        remove.disabled = true;
        void account.onRevoke(person.kind, person.id);
      });
      row.append(remove);
    }
    people.append(row);
  }

  const invite = el("div", "person-foot");
  const inviteBtn = el("button", "btn btn-primary", "Invite someone");
  inviteBtn.type = "button";
  inviteBtn.disabled = true;
  invite.append(inviteBtn);
  invite.append(
    el("span", "note", "Solicitors and registered advisers can be given read-only access."),
  );
  people.append(invite);
  frag.append(people);

  // ---------------------------------------------------------------- billing
  //
  // The design puts this inside Account rather than in the nav, and it is
  // linkable as `#billing` so anything blocked for want of payment can send
  // somebody straight here instead of describing where to go.
  const billing = sectionHead("Plan and payment");
  billing.id = "billing";
  frag.append(billing);

  const planCard = card("plan-card");
  const planRow = el("div", "plan-top");
  const planText = el("div");
  planText.append(el("div", "plan-name", account.tier ? account.tier.name : "No plan yet"));
  planText.append(
    el(
      "div",
      "plan-note",
      account.tier
        ? account.tier.summary
        : "Nothing has been chosen yet. Finish setting up your case and the plan will appear here.",
    ),
  );
  planRow.append(planText);
  // Displayed, never computed. The figure came from the server, so what is
  // shown here cannot drift from what would be charged.
  if (account.tier) planRow.append(el("div", "plan-price", account.tier.display));
  planCard.append(planRow);

  planCard.append(
    el("div", "plan-state", paymentLine(account.payment, account.paymentConfigured)),
  );
  planCard.append(
    el(
      "div",
      "plan-note",
      "The Home Office visa fee and the immigration health surcharge are paid to them, not to us.",
    ),
  );

  // Only where there is something to do. A button that cannot work is worse
  // than no button.
  const needsAction =
    account.paymentConfigured &&
    (!account.payment ||
      ["pending", "expired", "cancelled", "failed"].includes(account.payment.status));
  if (needsAction) {
    const pay = el(
      "button",
      "btn btn-primary",
      account.payment?.status === "pending" ? "Finish adding your card" : "Add your card",
    );
    pay.type = "button";
    pay.addEventListener("click", () => {
      pay.disabled = true;
      void account.onStartPayment();
    });
    planCard.append(pay);
  }
  frag.append(planCard);

  const receipts = el("div", "person-foot");
  const receiptsBtn = el("button", "btn", "Receipts");
  receiptsBtn.type = "button";
  receiptsBtn.disabled = true;
  receipts.append(receiptsBtn);
  receipts.append(el("span", "note", "Receipts appear here once there is a payment to show."));
  frag.append(receipts);

  frag.append(sectionHead("What Verity tells you about"));
  const toggles = el("div", "toggles");
  for (const notification of SAMPLE.account.notifications) {
    const row = el("label", "toggle-row");
    const body = el("div");
    body.append(el("div", "toggle-label", notification.label));
    body.append(el("div", "toggle-note", notification.note));
    row.append(body);
    const input = el("input", "toggle-input");
    input.type = "checkbox";
    input.checked = notification.on;
    input.disabled = true;
    row.append(input);
    row.append(el("span", "toggle"));
    toggles.append(row);
  }
  frag.append(toggles);

  frag.append(sectionHead("Your documents and data"));
  const data = el("div", "data-rows");

  const exportRow = el("div", "data-row");
  const exportBody = el("div");
  exportBody.append(el("div", "data-title", "Download everything"));
  exportBody.append(
    el("div", "data-note", "Every answer you have given and every result saved, as a file."),
  );
  exportRow.append(exportBody);
  const exportLink = el("a", "btn", "Download");
  exportLink.href = "/api/me/export";
  exportRow.append(exportLink);
  data.append(exportRow);

  const retention = el("div", "data-row");
  const retentionBody = el("div");
  retentionBody.append(el("div", "data-title", "How long we keep it"));
  retentionBody.append(
    el(
      "div",
      "data-note",
      "Seven years after your case concludes, then it is deleted. Immigration advisers are required to keep client files for that long, and your answers to the checker are the first part of that file.",
    ),
  );
  retention.append(retentionBody);
  data.append(retention);

  const del = el("div", "data-row");
  const delBody = el("div");
  delBody.append(el("div", "data-title", "Delete everything"));
  // WHAT THIS SAYS DEPENDS ON WHO ELSE IS ON THE CASE, and the honest version
  // is to say so before they press it rather than surprise them after. A
  // sponsor who expects to erase the case and instead removes only themselves
  // has been misled, and so has an applicant whose record was erased by
  // somebody else (F14 D2).
  delBody.append(
    el(
      "div",
      "data-note",
      // Another PERSON on the case, not an adviser and not an unaccepted
      // invitation — only a live second party changes what this button does.
      account.people.some((p) => p.kind === "member" && !p.is_you && p.role !== "adviser")
        ? "Removes you from this case and deletes everything that identifies you. Your partner's record stays, because it is theirs as well as yours. It cannot be undone."
        : "Removes your saved results, your answers and your account. It cannot be undone, and we cannot recover it afterwards.",
    ),
  );
  del.append(delBody);
  const delBtn = el("button", "btn btn-danger", "Delete");
  delBtn.type = "button";
  del.append(delBtn);
  data.append(del);

  // A confirm step, in the row itself. A `confirm()` dialog is easy to dismiss
  // by reflex and reads as browser furniture rather than as this decision.
  delBtn.addEventListener("click", () => {
    delBtn.hidden = true;
    const sure = el("div", "data-confirm");
    sure.append(el("span", "data-note", "Are you sure? This cannot be undone."));
    const yes = el("button", "btn btn-danger", "Yes, delete");
    const no = el("button", "btn btn-quiet", "Cancel");
    yes.type = "button";
    no.type = "button";
    no.addEventListener("click", () => {
      sure.remove();
      delBtn.hidden = false;
    });
    yes.addEventListener("click", () => {
      yes.disabled = true;
      no.disabled = true;
      void (async () => {
        const outcome = await account.onDelete();
        sure.replaceChildren(
          el(
            "span",
            "data-note",
            outcome === "erased"
              ? "Everything has been deleted. Signing you out."
              : outcome === "left"
                ? "You have been removed from this case. Signing you out."
                : outcome === "retained"
                  ? // NOT a refusal, and not dressed up as success either.
                    "Your access has ended and we will not contact you again. Because an adviser has worked on this case, we are required to keep the file for seven years after the case concludes. Signing you out."
                  : "We could not do that just now. Please try again, or email us.",
          ),
        );
      })();
    });
    sure.append(yes);
    sure.append(no);
    del.append(sure);
  });
  frag.append(data);

  return frag;
}
